import { stringUtils } from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps, ReactNode } from 'react'
import { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ComboBox,
  ComboBoxStateContext,
  Input,
  ListBox,
} from 'react-aria-components'

import { CircularSpinner } from '../../feedback'
import { useTranslate } from '../../hooks'
import { dropdownStyles } from '../../libs/dropdownStyles'
import { typographyStyles } from '../../structure/typography/styles'
import { ClearButton } from '../internal/clearButton/ClearButton'
import { clearButtonStyles } from '../internal/clearButton/clearButtonStyles'
import { InsideInputIconButton } from '../internal/insideInputIconButton/InsideInputIconButton'
import { rawTextInputStyles } from '../textInput/rawTextInputStyles'
import {
  defaultKeyFn,
  defaultRenderFn,
  filterItemsByText,
  findOrCreateValue,
} from './RawSelect.utils'
import { RawSelectOption } from './RawSelectOption'
import { SelectPopover } from './SelectPopover'
import { selectStyles } from './selectStyles'
import { useManageUserInput } from './useManageUserInput'
import { type SelectableItem, useSelectableItems } from './useSelectableItems'

const emptyMessageStyles = cx(
  'px-2 pt-1',
  typographyStyles({ intensity: 'tertiary' }),
)

/**
 * A custom select component.
 * Use when presenting the user for a selection between 3 and 10 ish
 * For fewer element use radio buttons
 * For more use combobox
 */
type Props<T> = Pick<
  ComponentProps<'input'>,
  'id' | 'name' | 'onBlur' | 'onClick' | 'aria-label' | 'aria-labelledby'
> & {
  /**
   * The options presented in the select. If the presented text differs from the actual value, use a list of Option objects otherwise just use strings
   */
  options: readonly T[]
  /**
   * Classname applied to the container element
   */
  className?: string
  /**
   * Classname applied to the input element
   */
  inputClassName?: string
  /**
   * Classname applied to each option in the select. Use to change e.g. break behaviour
   */
  optionClassName?: string
  /**
   * Classname applied to the dropdown in the select. Use to change e.g. width
   */
  optionsClassName?: string
  /**
   * Provides default styling to the input element. Ensuring that there is a consistent look and feel across the application.
   */
  isAddOn?: boolean
  /**
   * Displays the component in an error state
   */
  isInError?: boolean
  /**
   * Placeholder text shown when no value is selected
   */
  placeholder?: string
  /**
   * Whether to show a button for clearing the selected value. If true, a button will be shown that when clicked will clear the selected value and set the input value to an empty string.
   * @defaultValue `true`
   */
  shouldShowClearButton?: boolean
  /**
   * Disables the Select
   */
  disabled?: boolean
  /**
   * callback to handle value changes
   */
  onChange: (value: T | undefined) => void
  /**
   * A callback to pass the current value up when onBlur is called.
   * This is needed since the FocusEvent from <div> where the onBlur is called does not have the value.
   */
  onBlurValue?: (value: T | undefined) => void
  /**
   * The default selected value
   */
  value?: T
  /**
   * Function for rendering the contents of an item in the list
   */
  renderFn?: (item: T) => string | ReactNode
  /**
   * Function for rendering the current value. May be used to render a shortened version of the value to avoid the component dimensions
   * from growing when the dropdown is closed.
   */
  renderValueFn?: (item: T) => string | ReactNode
  /**
   * Function for retrieving the key for an item in the list
   */
  keyFn?: (item: T) => string
  /**
   * Skips DOM-based option text resolution. Use when the rendered options do
   * not need asynchronous text extraction for filtering or accessibility.
   * @defaultValue `false`
   */
  skipOptionResolution?: boolean
  /**
   * Indicates whether the select is currently loading options.
   */
  isLoading?: boolean
  /**
   * Called with the input text whenever the user types in the input.
   */
  onInputChange?: (inputText: string) => void
  /**
   * When there are no options available and the user has not searched. Shows default text telling the user to write something.
   */
  shouldShowAsyncSearchPromptWhenEmpty?: boolean
  /**
   * When we using async API requests one may not want to have both filtering enabled.
   */
  shouldSkipLocalFiltering?: boolean
  sortFn?: (
    left: { value: T; textValue: string },
    right: { value: T; textValue: string },
  ) => number
  /** Wraps the input inside ComboBox. Supply aria-labelledby when including a visible React Aria Label. */
  renderInput?: (input: ReactNode) => ReactNode
} & (
    | {
        /**
         * Creates a generic type T from an input string. This is the reverse of the renderFn and should only be provided
         * when isValueCreator is true.
         */
        valueFromString: (value: string) => T
        isValueCreator: true
      }
    | {
        valueFromString?: never
        isValueCreator?: never
      }
  )

export const RawSelect = <T,>({
  options,
  // By default this is set to true to easier accommodate non required fields
  shouldShowClearButton = true,
  placeholder,
  className,
  inputClassName,
  isAddOn = false,
  optionClassName,
  optionsClassName,
  isInError = false,
  disabled = false,
  onBlurValue,
  onChange,
  value,
  renderFn = defaultRenderFn,
  renderValueFn,
  keyFn = defaultKeyFn,
  id,
  name, // we deliberately do not pass the name as part of rest since we use id to link label and input for testing
  onBlur,
  onClick,
  isValueCreator,
  valueFromString = (value) => value as unknown as T,
  sortFn,
  skipOptionResolution = false,
  onInputChange,
  isLoading,
  shouldShowAsyncSearchPromptWhenEmpty,
  shouldSkipLocalFiltering = false,
  renderInput,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: Props<T>) => {
  const translate = useTranslate()
  const { items, extractionMirrors } = useSelectableItems({
    options,
    renderFn,
    renderValueFn,
    keyFn,
    skipOptionResolution: skipOptionResolution || !renderFn,
  })

  const hasValue = value !== undefined
  const valueKey = hasValue ? keyFn(value) : undefined
  const valueItem = items.find((item) => item.key === valueKey)

  const renderedValue =
    valueItem?.renderedInputValue ??
    (hasValue ? (renderValueFn?.(value) ?? renderFn(value)) : undefined)

  const syncedInputValue =
    valueItem?.textValue ??
    (hasValue && value !== null
      ? stringUtils.reactNodeToDisplayText(renderedValue)
      : '')

  const [isFocused, setIsFocused] = useState(false)
  const [nativeLabelIds, setNativeLabelIds] = useState<string>()
  const inputRef = useRef<HTMLInputElement>(null)
  const selectInputText = useManageUserInput({
    renderedValueFromItem: renderedValue,
    inputValueAsText: syncedInputValue,
    valueKey,
    isFocused,
  })
  const shouldShowClearAction = hasValue && !disabled && shouldShowClearButton

  const handleInputRef = (input: HTMLInputElement | null) => {
    inputRef.current = input
    if (!input) {
      return
    }

    // An input ID does not imply a label. Reference actual native labels so the
    // fallback cannot override them, while unlabeled inputs keep the fallback.
    const labelIds = ariaLabelledBy
      ? undefined
      : Array.from(input.labels ?? [], (label) => {
          label.id ||= crypto.randomUUID()
          return label.id
        }).join(' ') || undefined

    if (labelIds !== nativeLabelIds) {
      setNativeLabelIds(labelIds)
    }
  }

  // Selected after render since focusing can swap the value overlay for the real input text.
  useLayoutEffect(() => {
    if (isFocused) {
      inputRef.current?.select()
    }
  }, [isFocused])

  // Filter uses resolved plain text to support both string and ReactNode labels.
  const shouldFilter = selectInputText.isFiltering && !shouldSkipLocalFiltering
  const filterText = selectInputText.inputValue
  // Memoized so that re-renders of the surrounding form (e.g. typing in
  // another field) do not re-filter and re-sort every option.
  const visibleOptions = useMemo(() => {
    const filteredItems = shouldFilter
      ? filterItemsByText(items, filterText)
      : items
    return filteredItems.toSorted(sortFn)
  }, [items, shouldFilter, filterText, sortFn])

  const selectItem = (nextSelectedItem: SelectableItem<T> | undefined) => {
    const nextValue = nextSelectedItem?.value
    const nextInputValue = nextSelectedItem?.textValue ?? ''

    selectInputText.commit(nextInputValue)
    onChange(nextValue)
    onBlurValue?.(nextValue)
  }

  const handleCustomValue = (userInput: string) => {
    if (!isValueCreator) {
      return
    }

    const committedValue = findOrCreateValue({
      userInput,
      items,
      valueFromString,
    })

    if (!committedValue) {
      return
    }

    selectInputText.commit(committedValue.text)
    onChange(committedValue.value)
    onBlurValue?.(committedValue.value)
  }

  const selectInput = (
    <div className={cx('relative w-full', clearButtonStyles.group, className)}>
      <SelectInput
        ref={handleInputRef}
        id={id}
        name={name}
        disabled={disabled}
        data-autofocus
        className={cx(
          rawTextInputStyles({
            isInError,
            disabled,
          }),
          'break-all',
          shouldShowClearAction ? 'pe-11' : 'pe-6',
          isAddOn ? 'rounded-e-none!' : '',
          inputClassName,
        )}
        onChange={(event) => onInputChange?.(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={(event) => {
          setIsFocused(false)

          // On normal blur in creator mode, commit any free-text value
          if (selectInputText.isFiltering) {
            if (isValueCreator) {
              handleCustomValue(selectInputText.inputText)
            } else {
              selectInputText.clear()
            }
          }

          onBlur?.(event)
        }}
        onClick={onClick}
        placeholder={
          selectInputText.shouldShowInputValueOverlay || isFocused
            ? undefined
            : placeholder
        }
      />

      {selectInputText.shouldShowInputValueOverlay &&
        renderedValue !== undefined && (
          <div
            className={cx(
              'pointer-events-none absolute inset-y-0 inset-s-2 flex items-center truncate',
              shouldShowClearAction ? 'inset-e-14' : 'inset-e-8',
            )}
          >
            {/*
            Visual overlay for non-string selected content while input value stays
            plain-text for keyboard handling and filtering.
          */}
            {renderedValue}
          </div>
        )}
      <div
        className={cx(
          selectStyles.inputIconsWrapper,
          isInError && 'text-error-500',
        )}
      >
        {isLoading && <CircularSpinner className="size-4 m-0.5" />}
        {shouldShowClearAction && !isLoading && (
          <ClearButton
            onClear={() => {
              // Clear both visual and committed selection state.
              selectInputText.clear()
              onChange(undefined)
            }}
          />
        )}
        <InsideInputIconButton
          tip={null}
          iconName="ChevronUpDown"
          className="px-0!"
          isDisabled={disabled}
        />
      </div>
    </div>
  )

  return (
    <>
      <ComboBox
        aria-label={
          ariaLabel ??
          (nativeLabelIds || ariaLabelledBy
            ? undefined
            : translate('select.label'))
        }
        aria-labelledby={ariaLabelledBy ?? nativeLabelIds}
        isDisabled={disabled}
        onBlur={onBlur}
        menuTrigger="input"
        items={visibleOptions}
        allowsCustomValue={!!isValueCreator}
        // When doing asynchronous search, allow empty collection is necessary else the dropdown query will not appear.
        allowsEmptyCollection={!!onInputChange}
        inputValue={selectInputText.inputValue}
        value={
          visibleOptions.find((x) => x.key === valueItem?.key)?.key ?? null
        }
        onInputChange={(text) => {
          selectInputText.update(text)
          if (text.trim() === '') {
            onChange(undefined)
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !isValueCreator) {
            return
          }

          handleCustomValue(selectInputText.inputText)
          event.preventDefault()
        }}
        className="contents"
        onChange={(key) => {
          const nextSelectedItem = visibleOptions.find(
            (item) => item.key === key,
          )

          if (nextSelectedItem) {
            selectItem(nextSelectedItem)
          } else if (isValueCreator && typeof key === 'string') {
            handleCustomValue(key)
          }
        }}
        {...rest}
      >
        {renderInput ? renderInput(selectInput) : selectInput}
        <SelectPopover
          triggerRef={inputRef}
          offset={4}
          className={cx(
            selectStyles.options,
            'min-w-(--trigger-width) max-w-[min(calc(var(--trigger-width)*3),90vw)]!',
            optionsClassName,
            dropdownStyles,
          )}
        >
          {items.length === 0 && (
            <>
              {shouldShowAsyncSearchPromptWhenEmpty &&
                !selectInputText.isFiltering && (
                  <p className={emptyMessageStyles}>
                    {translate('select.pleaseTypeToSearch')}
                  </p>
                )}
              {selectInputText.isFiltering && (
                <p
                  className={cx(
                    emptyMessageStyles,
                    isLoading && 'invisible', // keeps the popover's height while loading
                  )}
                >
                  {translate('select.noResults')}
                </p>
              )}
            </>
          )}
          <ListBox className="outline-hidden">
            {(currentItem: SelectableItem<T>) => (
              <RawSelectOption
                currentItem={currentItem}
                disabled={disabled}
                optionClassName={optionClassName}
              />
            )}
          </ListBox>
        </SelectPopover>
      </ComboBox>
      {extractionMirrors}
    </>
  )
}

const SelectInput = ({ onClick, ...props }: ComponentProps<typeof Input>) => {
  const state = useContext(ComboBoxStateContext)

  return (
    <Input
      {...props}
      onClick={(event) => {
        state?.open(null, 'manual')
        onClick?.(event)
      }}
    />
  )
}
